import React, { useCallback, useMemo, useRef, useState } from 'react';
import cn from 'clsx';
import {
  ButtonWithGAClickButton,
  ModalMediaCardBody,
  ModalMediaCardFooter,
  ModalMediaCardGallery,
  ModalMediaCardImage,
  MoreInfoCard,
  MoreInfoCardBody,
  MoreInfoCardGrid,
  MoreInfoCardImage,
  SecondarySection,
  SectionSubTitle,
  Typography,
} from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { INTERIOR_DOORS_ELEMENT_BLOCK } from '../../interior-doors-constants';
import {
  interiorDoorsMediaCarBody,
  interiorDoorsMediaCardDescription,
  interiorDoorsModalMediaCardImage,
  interiorDoorsTrendsTextWrapper,
  interiorDoorsTrendsTitle,
} from './interior-doors-trends-section-styles';
import {
  InteriorDoorsMoreInfoCardStructure,
  INTERIOR_DOORS_TRENDS_CARD_LIST,
} from './interior-doors-trends-section-constants';

type InteriorDoorsTrendsSectionProps = {
  onConsultationSignupBtnClick: () => void;
};

export const InteriorDoorsTrendsSection = ({
  onConsultationSignupBtnClick,
}: InteriorDoorsTrendsSectionProps) => {
  const cardRefs = useRef<HTMLDivElement[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [openCardIndex, setOpenCardIndex] = useState<number | undefined>(undefined);

  const promotions = useMemo(
    () =>
      INTERIOR_DOORS_TRENDS_CARD_LIST.map(
        ({ title, name }: InteriorDoorsMoreInfoCardStructure, index: number) => ({
          name: INTERIOR_DOORS_ELEMENT_BLOCK,
          creative: name || title,
          position: (index + 1).toString(),
        })
      ),
    []
  );

  usePromoViewEvent(cardRefs, INTERIOR_DOORS_ELEMENT_BLOCK, promotions);

  /**
   * Обработчик закрытия модального окна
   */
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const handleCardChange = useCallback((index: number) => {
    setOpenCardIndex(index);
  }, []);

  /**
   * Обработчик нажатия на карточку со стилями
   */
  const handleCardClick = useCallback(
    (index: number) => () => {
      setOpenCardIndex(index);
      setIsModalOpen(true);
      analyticsUtils.promoClickEvent(INTERIOR_DOORS_ELEMENT_BLOCK, promotions[index]);
    },
    [promotions, setIsModalOpen, setOpenCardIndex]
  );

  const handleFindOutPriceBtnClick = useCallback(() => {
    setIsModalOpen(false);
    setOpenCardIndex(0);
    onConsultationSignupBtnClick();
  }, [onConsultationSignupBtnClick]);

  return (
    <SecondarySection>
      <div className={interiorDoorsTrendsTextWrapper}>
        <SectionSubTitle className={interiorDoorsTrendsTitle} margin="s">
          Тренды дизайна
        </SectionSubTitle>
        <Typography variant="m" size="body_long">
          У&nbsp;нас вы&nbsp;сможете выбрать стильную дверь, которая впишется в&nbsp;любой современный или
          классический интерьер.
        </Typography>
      </div>

      <MoreInfoCardGrid cardNumber={INTERIOR_DOORS_TRENDS_CARD_LIST.length} cardPerRow={2}>
        {INTERIOR_DOORS_TRENDS_CARD_LIST.map(({ imgSrc, title }, index) => (
          <MoreInfoCard
            key={createElementKey('mounting-type', index)}
            onClick={handleCardClick(index)}
            data-position={index}
            ref={(el: HTMLDivElement | null) => {
              if (el && cardRefs) cardRefs.current[index] = el;
            }}>
            <MoreInfoCardImage src={imgSrc} alt={title} title={title} />
            <MoreInfoCardBody title={title} wrapText />
          </MoreInfoCard>
        ))}
      </MoreInfoCardGrid>

      <ModalMediaCardGallery
        cardList={INTERIOR_DOORS_TRENDS_CARD_LIST}
        elementBlock={INTERIOR_DOORS_ELEMENT_BLOCK}
        isOpen={isModalOpen}
        openCardIndex={openCardIndex}
        onCardChange={handleCardChange}
        onClose={handleCloseModal}
        renderContent={({ description, modalImgSrc, title, modalImgClassName }) => (
          <>
            <ModalMediaCardBody className={interiorDoorsMediaCarBody} title={title}>
              <Typography
                adaptive={false}
                className={interiorDoorsMediaCardDescription}
                variant="m"
                size="body_long">
                {description}
              </Typography>

              <ModalMediaCardFooter>
                <ButtonWithGAClickButton
                  elementBlock={INTERIOR_DOORS_ELEMENT_BLOCK}
                  elementName="Узнать цену"
                  elementText={`${title} / modal`}
                  onClick={handleFindOutPriceBtnClick}
                  wide={false}>
                  Узнать цену
                </ButtonWithGAClickButton>
              </ModalMediaCardFooter>
            </ModalMediaCardBody>
            <ModalMediaCardImage
              alt={title}
              className={cn(interiorDoorsModalMediaCardImage, modalImgClassName)}
              src={modalImgSrc}
              title={title}
            />
          </>
        )}
      />
    </SecondarySection>
  );
};
